import { StandardEvents } from '@shopify/events';

/**
 * JAKE & IPI (2b-4 C6, QA C2/C4; C4d): announces once after a change made inside a cart
 * - a line that became sold in this update (C4d): "<title> on myyty. Poista se korista." or the count form, and
 * - the free shipping progress, if it changed.
 * Both go into one message, sold first, so the two never compete for the region.
 *
 * The visible free shipping text and the sold line text sit inside the cart (drawer or page), which is morphed on
 * every cart change, so they have no live role of their own (a sold line shown on page load is not announced).
 * The persistent `role="status"` region is rendered by the ji-cart-live snippet, once per page: inside the drawer's
 * modal <dialog> (anything outside an open modal dialog is inert and never heard) or, on the cart page, in the
 * layout. The region in the dialog where the change happened is used, otherwise the page region. Adding to the cart
 * elsewhere (product page, Quick Add) is not announced here: the product page has its own announcement.
 */
if (!window.jiCartLive) {
  window.jiCartLive = true;

  /** @param {Element | null | undefined} el */
  const clean = (el) => el?.textContent?.replace(/\s+/g, ' ').trim() ?? '';

  /** @param {Element} scope */
  const textOf = (scope) => clean(scope.querySelector('.ji-fs__text'));

  /** @param {Element} scope */
  const soldRows = (scope) => [...scope.querySelectorAll('[data-ji-sold]')];

  /** @param {Element} scope */
  const soldKeys = (scope) => new Set(soldRows(scope).map((row) => row.getAttribute('data-key') ?? ''));

  /** @param {Element} root - The cart-items-component where the change happened */
  const regionFor = (root) =>
    /** @type {HTMLElement | null} */ ((root.closest('dialog') ?? document).querySelector('[data-ji-cart-live]'));

  /**
   * Text for the lines that became sold since `before`, '' when none did.
   * @param {Element} scope
   * @param {Set<string>} before
   * @param {HTMLElement} region
   */
  const soldText = (scope, before, region) => {
    const rows = soldRows(scope).filter((row) => !before.has(row.getAttribute('data-key') ?? ''));
    if (rows.length === 0) return '';
    if (rows.length === 1) {
      const title = clean(rows[0].querySelector('.cart-items__title'));
      return (region.dataset.soldOne ?? '').replace('{{ title }}', () => title);
    }
    return (region.dataset.soldOther ?? '').replace('{{ count }}', String(rows.length));
  };

  /**
   * @param {HTMLElement} region
   * @param {string} text
   */
  const announce = (region, text) => {
    region.textContent = '';
    setTimeout(() => {
      region.textContent = text;
    }, 100);
  };

  /** @type {WeakMap<Element, { before: string, sold: Set<string>, stop: () => void }>} */
  const watching = new WeakMap();

  /**
   * Waits for the morph that follows a cart change and announces what changed (sold lines, free shipping text).
   * The section wrapper is watched, not the cart element: morphSection keeps the wrapper.
   * @param {Element} root - The cart-items-component that started the change
   */
  const watch = (root) => {
    const scope = root.closest('.shopify-section') ?? root;
    const current = watching.get(scope);
    if (current) current.stop();
    const before = current?.before ?? textOf(scope);
    const sold = current?.sold ?? soldKeys(scope);

    /** @type {ReturnType<typeof setTimeout> | undefined} */
    let settle;
    const check = () => {
      const region = regionFor(scope.querySelector('cart-items-component') ?? root);
      if (!region) return;
      const after = textOf(scope);
      const shipping = after && after !== before ? after : '';
      const message = [soldText(scope, sold, region), shipping].filter(Boolean).join(' ');
      if (!message) return;
      stop();
      announce(region, message);
    };
    const observer = new MutationObserver(() => {
      clearTimeout(settle);
      settle = setTimeout(check, 150);
    });
    const timeout = setTimeout(() => stop(), 6000);
    const stop = () => {
      observer.disconnect();
      clearTimeout(settle);
      clearTimeout(timeout);
      watching.delete(scope);
    };

    // data-ji-sold can arrive as an attribute on a kept row (hydration morph), not only in new nodes
    observer.observe(scope, {
      subtree: true,
      childList: true,
      characterData: true,
      attributeFilter: ['data-ji-sold'],
    });
    watching.set(scope, { before, sold, stop });
  };

  /** @param {Event} event */
  const onCartChange = (event) => {
    const root = event.target instanceof Element ? event.target.closest('cart-items-component') : null;
    if (root) watch(root);
  };

  document.addEventListener(StandardEvents.cartLinesUpdate, onCartChange);
  document.addEventListener(StandardEvents.cartDiscountUpdate, onCartChange);
}
